import { Card, PageContainer, type OptionProps } from '@amelio/ui-library';
import { motion } from 'motion/react';

import { QuestionForm } from './QuestionForm/QuestionForm';
import { QuestionList } from './QuestionList/QuestionList';
import { QuestionnaireHeader } from './QuestionnaireHeader/QuestionnaireHeader';
import { SelectedQuestionBanner } from './SelectedQuestionBanner/SelectedQuestionBanner';
import { Toolbar } from './Toolbar/Toolbar';

import type { QuestionActionItemsProps } from '../types';
import type { AdvancedOptionFilterType } from '@/constants/questionnaire/Filters';
import type { ReviewQuestionStatusType } from '@/constants/questionnaire/Question';
import type { QuestionTypeValue } from '@/constants/QuestionType';
import type { ReviewTemplateStatus } from '@/constants/review/ReviewTemplateStatus';
import type { User } from '@/types/directory/User';
import type { ReviewQuestion } from '@/types/review/ReviewQuestion';
import type { DraggableItem } from '@amelio/ui-library';
import type { ReactNode } from 'react';

import { type EvaluationTypeValue } from '@/constants/cycle-evaluation/CycleEvaluation';
import { type ManagerAssessmentSkillSelectorOptionValue } from '@/constants/review/ManagerAssessmentSkillSelectorOption';

const EDITOR_OPEN_ANIMATION_DURATION_S = 0.4;
const EDITOR_OPEN_ANIMATION_Y_OFFSET_PX = -8;

interface QuestionnaireCreationProps {
    reviewees?: User[];
    filterOptions: OptionProps[];
    selectedFilters: string[];
    advancedOptionsFilters: AdvancedOptionFilterType[];
    questionTypeFilters: QuestionTypeValue[];
    handleFilterChange: (value: string) => void;
    isAddingQuestion: boolean;
    questions: ReviewQuestion[];
    selectedQuestionId: string | null;
    currentQuestion?: ReviewQuestion;
    questionActionItems: QuestionActionItemsProps[];
    isEditingTitle: boolean;
    title: string;
    isPendingUpdateQuestion: boolean;
    isPendingUpdateQuestionnaire: boolean;
    isPendingCreateQuestion: boolean;
    isPendingDeleteQuestion?: boolean;
    setIsEditingTitle: (value: boolean) => void;
    onCancelTitle: () => void;
    onTitleChange: (title: string) => void;
    onQuestionTypeFilterChange: (filter: QuestionTypeValue) => void;
    onAdvancedOptionsFilterChange: (filter: AdvancedOptionFilterType) => void;
    onPreviewClick?: () => void;
    onAddQuestionClick: () => void;
    onCancelEdit: () => void;
    onDeleteQuestion?: (question: ReviewQuestion) => void;
    onQuestionChange: (question: ReviewQuestion) => void;
    onReorderQuestions: (items: DraggableItem<ReviewQuestion>[]) => void;
    onUpdateQuestion: (question: ReviewQuestion) => void;
    onCreateQuestion: (question: ReviewQuestion) => void;
    onToggleQuestionStatus?: (
        question: ReviewQuestion,
        newStatus: ReviewQuestionStatusType,
    ) => void;
    onRowClick?: (question: ReviewQuestion) => void;
    isTemplateCreationPage?: boolean;
    onTranslateClick?: () => void;
    onSelectTemplateClick?: () => void;
    reviewType?: EvaluationTypeValue;
    topContent?: ReactNode;
    bottomContent?: ReactNode;
    skillSelectorOption?: ManagerAssessmentSkillSelectorOptionValue;
    templateStatus?: ReviewTemplateStatus;
    templateActions?: OptionProps[];
    onTemplateActionClick?: (actionId: string) => void;
}

export const QuestionnaireCreation = ({
    reviewees,
    isAddingQuestion,
    questions,
    selectedQuestionId,
    currentQuestion,
    questionActionItems,
    isEditingTitle,
    title,
    isPendingUpdateQuestion,
    isPendingUpdateQuestionnaire,
    isPendingCreateQuestion,
    isPendingDeleteQuestion,
    setIsEditingTitle,
    onCancelTitle,
    onTitleChange,
    onQuestionTypeFilterChange,
    onAdvancedOptionsFilterChange,
    onPreviewClick,
    onAddQuestionClick,
    onCancelEdit,
    onQuestionChange,
    onReorderQuestions,
    onUpdateQuestion,
    onCreateQuestion,
    onToggleQuestionStatus,
    onRowClick,
    filterOptions,
    selectedFilters,
    advancedOptionsFilters,
    questionTypeFilters,
    handleFilterChange,
    isTemplateCreationPage = false,
    onTranslateClick,
    templateStatus,
    templateActions,
    onTemplateActionClick,
    onSelectTemplateClick,
    reviewType,
    topContent,
    bottomContent,
    skillSelectorOption,
}: QuestionnaireCreationProps) => {
    const selectedIndex =
        selectedQuestionId !== null ? questions.findIndex((q) => q.id === selectedQuestionId) : -1;
    const selectedQuestion = selectedIndex >= 0 ? questions[selectedIndex] : null;
    const isEditingQuestion = selectedQuestion !== null;
    const editorSlotAfterId = isEditingQuestion ? (selectedQuestion.id ?? null) : undefined;
    const hiddenQuestionId = selectedQuestion?.id;
    const previewEvaluee = reviewees?.[0];

    const renderEditorSlot = () => (
        <motion.div
            key={selectedQuestion?.id ?? 'editor'}
            initial={{ opacity: 0, y: EDITOR_OPEN_ANIMATION_Y_OFFSET_PX }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: EDITOR_OPEN_ANIMATION_DURATION_S, ease: 'easeOut' }}
            className={'bg-gradient-editor-panel w-full rounded-2xl border border-white p-6'}
            data-testid={'question-editor-slot-content'}
        >
            <SelectedQuestionBanner
                question={selectedQuestion}
                previewEvaluee={previewEvaluee}
            />
            <div className={'mt-4'}>
                <QuestionForm
                    mode={'edit'}
                    question={currentQuestion}
                    onQuestionChange={onQuestionChange}
                    onCancel={onCancelEdit}
                    onSave={onUpdateQuestion}
                    isPendingUpdateQuestion={isPendingUpdateQuestion}
                    reviewType={reviewType}
                    validateOnSave={true}
                />
            </div>
        </motion.div>
    );

    return (
        <PageContainer className={'flex w-full flex-1 flex-col gap-6'}>
            <QuestionnaireHeader
                reviewees={reviewees || []}
                previewEvaluee={previewEvaluee}
                title={title}
                isPendingUpdateQuestionnaire={isPendingUpdateQuestionnaire}
                onPreviewClick={onPreviewClick}
                isEditingTitle={isEditingTitle}
                setIsEditingTitle={setIsEditingTitle}
                onCancelTitle={onCancelTitle}
                onTitleChange={onTitleChange}
                isTemplateCreationPage={isTemplateCreationPage}
                onSelectTemplateClick={onSelectTemplateClick}
                templateStatus={templateStatus}
                templateActions={templateActions}
                onTemplateActionClick={onTemplateActionClick}
            />

            {topContent}

            <Card
                className={'flex w-full flex-col gap-4'}
                borderRadius={'2xl'}
            >
                <Toolbar
                    selectedFilters={selectedFilters || []}
                    filterOptions={filterOptions || []}
                    advancedOptionsFilters={advancedOptionsFilters || []}
                    questionTypeFilters={questionTypeFilters || []}
                    handleFilterChange={handleFilterChange}
                    onAddQuestionClick={onAddQuestionClick}
                    onQuestionTypeFilterChange={onQuestionTypeFilterChange}
                    onAdvancedOptionsFilterChange={onAdvancedOptionsFilterChange}
                    onTranslateClick={onTranslateClick}
                />

                {!isEditingQuestion && isAddingQuestion && (
                    <QuestionForm
                        mode={'add'}
                        question={currentQuestion}
                        onQuestionChange={onQuestionChange}
                        onCancel={onCancelEdit}
                        onSave={onCreateQuestion}
                        isPendingCreateQuestion={isPendingCreateQuestion}
                        reviewType={reviewType}
                        validateOnSave={true}
                    />
                )}

                <QuestionList
                    questions={questions}
                    actionItems={questionActionItems || []}
                    isPendingDeleteQuestion={isPendingDeleteQuestion || false}
                    onReorder={onReorderQuestions}
                    onStatusToggle={onToggleQuestionStatus}
                    onRowClick={onRowClick}
                    editorSlotAfterId={editorSlotAfterId}
                    renderEditorSlot={isEditingQuestion ? renderEditorSlot : undefined}
                    hiddenQuestionId={hiddenQuestionId}
                    isTemplateCreationPage={isTemplateCreationPage}
                    previewEvaluee={previewEvaluee}
                    skillSelectorOption={skillSelectorOption}
                />
                {bottomContent}
            </Card>
        </PageContainer>
    );
};

export default QuestionnaireCreation;
